import React, { useState, ReactNode, useEffect } from 'react';
import { cn } from '@/shared/utils/utils';

interface TabLayoutProps {
  children: ReactNode;
}

interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  defaultTab?: string;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({ tabs, defaultTab, className }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);

  // Handle URL hash navigation for tabs
  useEffect(() => {
    const getTabFromHash = () => {
      const hash = window.location.hash.replace('#', '');
      return hash && tabs.some((tab) => tab.id === hash) ? hash : null;
    };

    // Set initial tab from hash if available
    const hashTab = getTabFromHash();
    if (hashTab) {
      setActiveTab(hashTab);
    }

    // Listen for hash changes
    const handleHashChange = () => {
      const hashTab = getTabFromHash();
      if (hashTab) {
        setActiveTab(hashTab);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []); // Removed tabs dependency to prevent infinite loop

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    // Update URL hash when tab is clicked
    window.history.replaceState(null, '', `#${tabId}`);
  };

  return (
    <div className={cn('w-full h-full flex flex-col overflow-hidden', className)}>
      <div className="flex-shrink-0 bg-background/80 backdrop-blur-xl border-b border-border w-full overflow-x-auto">
        <div className="px-6 pt-6 pb-4 w-full max-w-full">
          <nav
            className="flex w-fit max-w-full bg-muted rounded-xl p-1.5 backdrop-blur-sm"
            aria-label="Tabs"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={cn(
                  'flex items-center justify-center min-w-0 flex-shrink text-center font-semibold text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-opacity-20 rounded-lg px-6 py-2.5 mx-0.5',
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
                )}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                <span className="relative z-10 truncate">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden bg-background/30">{activeTabContent}</div>
    </div>
  );
};

const TabLayout: React.FC<TabLayoutProps> = ({ children }) => {
  return <div className="h-full w-full bg-background">{children}</div>;
};

export default TabLayout;
